import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS, NAV_ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { messages } from './SkuItemInReviewModal.messages';
import { suppressInReviewModal } from './SkuItemInReviewModal.helpers';

import './SkuItemInReviewModal.scss';

const { block, element } = bem('sku-item-in-review-modal');

export const SKU_ITEM_IN_REVIEW_MODAL_NAME = 'SkuItemInReviewModal';

function SkuItemInReviewModal({ itemName }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  function handleClose() {
    if (dontShowAgain) {
      suppressInReviewModal();
    }
    dispatch(closeModal(SKU_ITEM_IN_REVIEW_MODAL_NAME));
  }

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h3 className={element('title')}>
          <Message message={messages.title} />
        </h3>
        <span
          className={element('close-icon')}
          onClick={handleClose}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClose();
            }
          }}
          role="button"
          aria-label={intl.formatMessage(messages.dismissAriaLabel)}
          tabIndex={0}>
          <Isvg src={ICONS.cross} />
        </span>
      </header>
      <section className={element('content')}>
        <div className={element('banner')}>
          <div className={element('banner-content-container')}>
            <span className={element('item-name')}>{itemName}</span>
            <div className={element('faux-dropdown')}>
              <span className={element('status-wrapper')}>
                <Isvg src={ICONS.clock} className={element('status-marker')} />
                <When predicate={!isMobile}>
                  <Message message={messages.pendingReview} />
                </When>
              </span>
              <SvgArrow direction="down" theme="disabled" />
            </div>
          </div>
        </div>
        <Message message={messages.whatToExpect} />
        <div className={element('bullet-container')}>
          <span className={element('bullet-point')}>
            <Isvg src={ICONS.searchGlass} className={element('icon')} />
            <Message message={messages.teamIsReviewing} values={{ strong: text => <strong>{text}</strong> }} />
          </span>
          <span className={element('bullet-point')}>
            <Isvg src={ICONS.eyeWithCrossDark} className={element('icon')} />
            <Message message={messages.customersCannotSee} values={{ strong: text => <strong>{text}</strong> }} />
          </span>
          <span className={element('bullet-point')}>
            <Isvg src={ICONS.clock} className={element('icon', 'clock-icon')} />
            <Message message={messages.expectedTimeInReview} values={{ strong: text => <strong>{text}</strong> }} />
          </span>
          <span className={element('bullet-point')}>
            <Isvg src={NAV_ICONS.appointments} className={element('icon')} />
            <Message message={messages.receiveAnEmail} values={{ strong: text => <strong>{text}</strong> }} />
          </span>
        </div>
        <When predicate={!isMobile}>
          <label className={element('checkbox-row')}>
            <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} />
            <Message message={messages.dontShowAgain} />
          </label>
        </When>
      </section>
      <footer className={element('footer')}>
        <Message
          as={Button}
          className={element('close-btn')}
          message={messages.close}
          onClick={handleClose}
          styled="primary"
          type={Button.TYPES.BUTTON}
        />
      </footer>
    </div>
  );
}

SkuItemInReviewModal.propTypes = {
  itemName: PropTypes.string.isRequired,
};

export default SkuItemInReviewModal;
